import './AttachFilesBlock.scss';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { MdOutlineInfo, MdOutlineErrorOutline, MdClose } from 'react-icons/md';
import { IAttachesCreateModel } from '@models/attaches/IAttachesCreateModel';
import { IFileData } from '@srm/ui/types/IFileData';
import { v4 as uuidv4, validate as uuidValidate } from 'uuid';
import Button from '@srm/ui/button';
import { ActionIcon } from '@mantine/core';
import InputFile from '@srm/ui/input-file';
import { checkFilesSize, getAcceptExtensionString } from '@utils/helpers';
import { formatBytes } from '@srm/ui/lib/formatBytes';
import ViewSwitcher, { IViewSwitcherOption, ViewIconCards, ViewIconTable } from '@srm/ui/view-switcher';
import clsx from 'clsx';
import { ISelectedFiles } from './ISelectedFiles';
import { setFileNameKeepingExtension } from './attachFileName';
import { MAX_FILE_SIZE } from '@/constants';
import AttachFileTile, { IAttachFileField } from './parts/AttachFileTile/AttachFileTile';

type AttachFilesView = 'table' | 'tiles';

const VIEW_OPTIONS: IViewSwitcherOption<AttachFilesView>[] = [
    { key: 'table', label: 'Список', title: 'Список', Icon: ViewIconTable },
    { key: 'tiles', label: 'Карточки', title: 'Карточки с превью', Icon: ViewIconCards },
];

const VIEW_STORAGE_KEY = 'attachFilesBlock.view';

const readStoredView = (): AttachFilesView => {
    try {
        return localStorage.getItem(VIEW_STORAGE_KEY) === 'tiles' ? 'tiles' : 'table';
    } catch {
        return 'table';
    }
};

export interface IAttachFilesBlockRefActions {
    getData: () => ISelectedFiles;
    setData: (data: ISelectedFiles) => void;
    isValid: () => boolean;
}

export interface IAttachFilesBlockProps {
    ref?: React.Ref<IAttachFilesBlockRefActions>;
    id?: string;
    model: IAttachesCreateModel;
    onChanged?: (data: ISelectedFiles) => void;
    onChangeFilesSizeExceeded?: (value: boolean) => void;
    /** Подпись под панелью, например «Файлы сохранятся вместе с документом» у черновика. */
    note?: React.ReactNode;
    /** Блок обязателен: маркер требования и красная рамка дропзоны при ошибке. */
    required?: boolean;
    /** Ошибка обязательности от формы (ограничения блок показывает сам). */
    error?: string;
}

const pluralizeFiles = (n: number): string => {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'файл';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'файла';
    return 'файлов';
};

const buildAcceptString = (model: IAttachesCreateModel): string => {
    if (!model?.categories || model.categories.length === 0) return '*';
    // если есть хотя бы одна категория без ограничений — '*'
    if (model.categories.find((cat) => cat?.allowedExtensions == null || !cat?.allowedExtensions?.trim())) {
        return '*';
    }
    return getAcceptExtensionString(
        model.categories
            .map((cat) => cat?.allowedExtensions)
            .filter((ext) => ext)
            .join(','),
    );
};

export const AttachFilesBlock: React.FC<IAttachFilesBlockProps> = ({ ref, ...props }) => {
    const [errorText, setErrorText] = useState<string>();
    const [inputErrors, setInputErrors] = useState<string[]>([]);
    const [removed, setRemoved] = useState<{ row: any; file?: File; index: number }>();
    const rootRef = useRef<HTMLDivElement>(null);
    const [dataSourceFiles, setDataSourceFiles] = useState<any[]>([]);
    const [deleteFiles, setDeleteFiles] = useState<any[]>([]);
    const [binaryFiles, setBinaryFiles] = useState<{ [fileId: string]: File }>({});
    const [filesSizeExceeded, setFilesSizeExceeded] = useState<boolean>(false);
    const [view, setView] = useState<AttachFilesView>(readStoredView);

    const onViewChange = useCallback((next: AttachFilesView) => {
        setView(next);
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, next);
        } catch {
            // приватный режим / запрет хранилища — вид просто не запомнится
        }
    }, []);

    const maxSizeBytes = MAX_FILE_SIZE ? MAX_FILE_SIZE * 1024 : undefined;

    useEffect(() => {
        props.onChangeFilesSizeExceeded?.(filesSizeExceeded);
    }, [filesSizeExceeded, props.onChangeFilesSizeExceeded]);

    useEffect(() => {
        if (removed) rootRef.current?.querySelector<HTMLButtonElement>('.attach-files-block__undo button')?.focus();
    }, [removed]);

    useEffect(() => {
        onRowUpdated();
    }, [deleteFiles, dataSourceFiles]);

    const getUpdatedFiles = useCallback((): ISelectedFiles => {
        let binaryFilesToUpload = dataSourceFiles.map((fileData) => {
            return binaryFiles[fileData._fileId];
        });

        let filesDataToUpload = dataSourceFiles.map((x) => {
            return { ...x };
        });
        deleteFiles.forEach((delItem) => {
            let clone = { ...delItem };
            clone._actType = 'delete';
            filesDataToUpload.push(clone);
        });

        return {
            attachProperties: filesDataToUpload,
            files: binaryFilesToUpload,
        };
    }, [binaryFiles, dataSourceFiles, deleteFiles]);

    const onRowUpdated = useCallback(() => {
        let data = getUpdatedFiles();
        props.onChanged && props.onChanged(data);
    }, [getUpdatedFiles, props]);

    /** Правка свойства файла: имя файла держит исходное расширение (EUPDEV-12263). */
    const onCellCommit = useCallback((row: any, field: string, value: unknown) => {
        setDataSourceFiles((prev) =>
            prev.map((file) => {
                if (file._fileId !== row._fileId) return file;
                const next = { ...file };
                if (field === 'AttachmentFileName') setFileNameKeepingExtension(next, value, file);
                else next[field] = value;
                return next;
            }),
        );
    }, []);

    const onRowRemoved = useCallback(
        (row: any) => {
            const removedFileId = row._fileId;
            setRemoved({
                row: { ...row },
                file: binaryFiles[removedFileId],
                index: dataSourceFiles.findIndex((f) => f._fileId === removedFileId),
            });
            setErrorText(undefined);
            const newBinaryFiles = { ...binaryFiles };
            if (removedFileId && uuidValidate(removedFileId) && binaryFiles[removedFileId]) {
                delete newBinaryFiles[removedFileId];
                setBinaryFiles(newBinaryFiles);
            }
            setDataSourceFiles((prev) => prev.filter((file) => file._fileId !== removedFileId));
            // уже сохранённый файл (не новый uuid) уходит на сервер отдельным «удалить»
            if (!uuidValidate(removedFileId)) {
                setDeleteFiles((deleteFiles) => [...deleteFiles, row]);
            }

            setFilesSizeExceeded(!checkFilesSize(Object.values<File>(newBinaryFiles), maxSizeBytes));
        },
        [binaryFiles, maxSizeBytes, dataSourceFiles],
    );

    const restoreRemoved = () => {
        if (!removed) return;
        const nextBinaryFiles = { ...binaryFiles };
        if (removed.file) nextBinaryFiles[removed.row._fileId] = removed.file;
        if (!checkFilesSize(Object.values(nextBinaryFiles), maxSizeBytes)) {
            setErrorText(`Не удалось восстановить файл: превышен общий лимит ${formatBytes(maxSizeBytes!)}.`);
            return;
        }
        setBinaryFiles(nextBinaryFiles);
        setDataSourceFiles((prev) => {
            const next = [...prev];
            next.splice(Math.min(removed.index, next.length), 0, removed.row);
            return next;
        });
        setDeleteFiles((prev) => prev.filter((f) => f._fileId !== removed.row._fileId));
        setRemoved(undefined);
        setErrorText(undefined);
        setFilesSizeExceeded(false);
        rootRef.current?.querySelector<HTMLButtonElement>('.rf-file-input__compact-actions button')?.focus();
    };

    useImperativeHandle(ref, () => ({
        getData: (): ISelectedFiles => getUpdatedFiles(),
        setData: (data: ISelectedFiles) => {
            setDataSourceFiles(data.attachProperties.filter((f) => f._actType !== 'delete'));
            setDeleteFiles(data.attachProperties.filter((f) => f._actType === 'delete'));
            const restored: Record<string, File> = {};
            data.attachProperties.forEach((row, index) => {
                if (row._actType === 'delete') return;
                const file = data.files[index] instanceof File ? data.files[index] : binaryFiles[row._fileId];
                if (file) restored[row._fileId] = file;
            });
            setBinaryFiles(restored);
            setFilesSizeExceeded(!checkFilesSize(Object.values(restored), maxSizeBytes));
            setRemoved(undefined);
        },
        isValid: (): boolean => {
            setErrorText(undefined);
            return !filesSizeExceeded && checkFilesForRestrictions();
        },
    }));

    const setFile = async (files: IFileData[]) => {
        setErrorText(undefined);
        let newBinaryFiles = { ...binaryFiles };

        if (files && props?.model?.properties && props?.model?.categories) {
            for (let i = 0; i < files.length; i++) {
                let f = files[i].file;

                let fileGuid = uuidv4();

                if (newBinaryFiles && f) {
                    newBinaryFiles[fileGuid] = f;
                }

                let fileObj: any = {
                    _fileId: fileGuid,
                    _actType: 'add',
                    _filename: f.name,
                    _fileSize: f.size,
                    _category_: props?.model?.categories[0]?.name,
                };
                for (let j = 0; j < props?.model?.properties?.length; j++) {
                    let col = props?.model?.properties[j];
                    let keyName = col.key;

                    switch (keyName) {
                        case 'AttachmentFileName':
                            fileObj[keyName] = f?.name;
                            break;
                        default: {
                            let foundDefaultValue = props?.model?.defaultValues?.find((x) => x.attr == keyName);
                            if (foundDefaultValue) {
                                fileObj[keyName] = foundDefaultValue.val;
                            } else {
                                if (
                                    props?.model.dictionaries &&
                                    col.dictName &&
                                    props?.model.dictionaries[col.dictName]
                                ) {
                                    if (props?.model.dictionaries[col.dictName].length > 0)
                                        fileObj[keyName] = props?.model.dictionaries[col.dictName][0].code;
                                } else {
                                    fileObj[keyName] = undefined;
                                }
                            }
                        }
                    }
                }

                if (props.model?.defaultValues) {
                    let customProps = props.model?.defaultValues.filter((x) => x.attr.startsWith('_custom_property_'));
                    customProps.forEach((customProp) => {
                        let customVal = customProp.val;

                        switch (customProp.val) {
                            case '@guid()':
                                customVal = uuidv4();
                                break;
                        }

                        fileObj[customProp.attr] = customVal;
                    });
                }

                setDataSourceFiles((dataSourceFiles) => [...dataSourceFiles, fileObj]);
            }

            setBinaryFiles(newBinaryFiles);
        }

        setFilesSizeExceeded(!checkFilesSize(Object.values<File>(newBinaryFiles), maxSizeBytes));
    };

    const checkFilesForRestrictions = (): boolean => {
        let extErrors: string[] = [];
        if (dataSourceFiles) {
            dataSourceFiles.forEach((f) => {
                let fCat = f._category_;
                let fExt = f.AttachmentFileName?.split('.')?.pop()?.toLowerCase();
                let foundCat = props?.model?.categories.find((x) => x.name == fCat);
                if (fExt && foundCat && foundCat.allowedExtensions) {
                    if (!foundCat.allowedExtensions.split(',').some((x) => x.trim().toLowerCase() == fExt)) {
                        extErrors.push(
                            `Файл '${f.AttachmentFileName}' не может находиться в категории '${foundCat.name}'`,
                        );
                    }
                }

                if (props?.model?.restrictions) {
                    props?.model?.restrictions.forEach((r) => {
                        if (f[r.ifAttachAttrName] == r.ifAttachAttrValue) {
                            if (
                                (r.checkAttrName && f[r.checkAttrName] != r.checkAttrValue) ||
                                (r.checkExtensions &&
                                    !r.checkExtensions.split(',').some((x) => x.toLowerCase() == fExt)) ||
                                (r.checkFileSizeInBytes &&
                                    r.checkFileSizeInBytes < (binaryFiles[f._fileId]?.size ?? f._fileSize ?? 0))
                            ) {
                                extErrors.push(`Файл '${f.AttachmentFileName}' ${r.checkMessage}`);
                            }
                        }
                    });
                }
            });
        }

        if (extErrors.length > 0) {
            setErrorText(extErrors.join('; '));
            return false;
        } else {
            return true;
        }
    };

    const acceptString = useMemo(() => buildAcceptString(props.model), [props.model]);

    const totalSize = useMemo(
        () =>
            dataSourceFiles
                .filter((f) => binaryFiles[f._fileId])
                .reduce((sum, f) => sum + (binaryFiles[f._fileId]?.size ?? 0), 0),
        [dataSourceFiles, binaryFiles],
    );

    const newFilesCount = useMemo(
        () => dataSourceFiles.filter((f) => binaryFiles[f._fileId]).length,
        [dataSourceFiles, binaryFiles],
    );

    /** Свойства для плитки: те же, что колонки таблицы, кроме имени (оно — заголовок плитки). */
    const tileFields = useMemo<IAttachFileField[]>(() => {
        const dictionaries = props.model?.dictionaries;
        const fields: IAttachFileField[] = (props.model?.properties ?? [])
            .filter((col) => !col.hidden && col.key !== 'AttachmentFileName')
            .map((col) => {
                const dictionary = col.dictName ? dictionaries?.[col.dictName] : undefined;
                return {
                    key: col.key,
                    label: col.displayName,
                    options: dictionary?.map((item) => ({ value: item.code, label: item.code })),
                };
            });
        const categories = props.model?.categories ?? [];
        if (categories.length > 1) {
            fields.push({
                key: '_category_',
                label: 'Категория',
                options: categories.map((item) => ({ value: item.name, label: item.name })),
            });
        }
        return fields;
    }, [props.model]);

    const nameEditable = useMemo(
        () => !!props.model?.properties?.some((col) => col.key === 'AttachmentFileName' && !col.hidden),
        [props.model],
    );

    const percent = maxSizeBytes ? Math.min(100, Math.round((totalSize / maxSizeBytes) * 100)) : 0;
    const usageState: 'idle' | 'warn' | 'over' =
        !maxSizeBytes || totalSize === 0 ? 'idle' : totalSize > maxSizeBytes ? 'over' : percent >= 80 ? 'warn' : 'idle';

    const sizeError =
        filesSizeExceeded && maxSizeBytes
            ? `Превышен общий лимит ${formatBytes(maxSizeBytes)}. Уберите лишние файлы или уменьшите их размер.`
            : undefined;
    const invalid = !!(sizeError || errorText || props.error || inputErrors.length);
    const messages = [...new Set([sizeError, errorText, ...inputErrors].filter((text): text is string => !!text))];

    return (
        <div
            ref={rootRef}
            className={clsx(
                'attach-files-block',
                invalid && 'attach-files-block--invalid',
                tileFields.length > 3 && 'attach-files-block--many-fields',
                nameEditable && 'attach-files-block--renamable',
            )}
            style={
                {
                    '--attach-file-columns': `minmax(160px, 1.25fr) ${tileFields.map(() => 'minmax(0, 1fr)').join(' ')} var(--attach-file-actions)`,
                } as React.CSSProperties
            }
            data-testid={props.id ? `attach-files-block-${props.id}` : undefined}
        >
            {props.error ? (
                <p className="attach-files-block__requirement attach-files-block__requirement--error" role="alert">
                    <MdOutlineErrorOutline aria-hidden />
                    {props.error}
                </p>
            ) : props.required && dataSourceFiles.length === 0 ? (
                <p className="attach-files-block__requirement">
                    <span className="attach-files-block__requirement-mark" aria-hidden>
                        *
                    </span>
                    Приложите хотя бы один файл
                </p>
            ) : null}
            <InputFile
                compact
                sizeLimitLabel="суммарно"
                className="attach-files-block__input"
                invalid={invalid}
                onChange={setFile}
                onError={(error) => setInputErrors((prev) => [...prev, error.message])}
                maxSize={MAX_FILE_SIZE}
                multiple
                showChips={false}
                clearFilesAfterSet
                accept={acceptString}
            />
            {messages.length > 0 && (
                <div className="attach-files-block__error" role="alert">
                    <MdOutlineErrorOutline aria-hidden />
                    <div>
                        {messages.map((message) => (
                            <p key={message}>{message}</p>
                        ))}
                    </div>
                    {!sizeError && (
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label="Скрыть сообщение об ошибке"
                            onClick={() => {
                                setInputErrors([]);
                                setErrorText(undefined);
                            }}
                        >
                            <MdClose />
                        </ActionIcon>
                    )}
                </div>
            )}
            {dataSourceFiles.length > 0 && (
                <>
                    <div className="attach-files-block__bar">
                        <span aria-live="polite">
                            Выбрано: {dataSourceFiles.length} {pluralizeFiles(dataSourceFiles.length)}
                        </span>
                        <ViewSwitcher
                            className="attach-files-block__view"
                            value={view}
                            options={VIEW_OPTIONS}
                            onChange={onViewChange}
                            ariaLabel="Вид списка файлов"
                        />
                    </div>
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Scrollable region must support keyboard scrolling. */}
                    <div className="attach-files-block__scroll" role="region" aria-label="Выбранные файлы" tabIndex={0}>
                        {view === 'table' && (
                            <div className="attach-files-block__columns" aria-hidden>
                                <span>Файл</span>
                                {tileFields.map((f) => (
                                    <span key={f.key}>{f.label}</span>
                                ))}
                                <span />
                            </div>
                        )}
                        <div className={view === 'tiles' ? 'attach-files-block__tiles' : 'attach-files-block__list'}>
                            {dataSourceFiles.map((row) => (
                                <AttachFileTile
                                    key={row._fileId}
                                    row={row}
                                    file={binaryFiles[row._fileId]}
                                    fields={tileFields}
                                    nameEditable={nameEditable}
                                    onCommit={onCellCommit}
                                    onRemove={onRowRemoved}
                                    layout={view === 'tiles' ? 'card' : 'row'}
                                />
                            ))}
                        </div>
                    </div>
                    {newFilesCount > 0 && (
                        <div className={clsx('attach-files-block__usage', `attach-files-block__usage--${usageState}`)}>
                            <span>Общий размер{newFilesCount !== dataSourceFiles.length ? ' новых файлов' : ''}</span>
                            <div className="attach-files-block__usage-value">
                                <span>
                                    {formatBytes(totalSize)}
                                    {maxSizeBytes !== undefined && <> из {formatBytes(maxSizeBytes)}</>}
                                </span>
                                {maxSizeBytes !== undefined && (
                                    <div
                                        className="attach-files-block__usage-bar"
                                        role="meter"
                                        aria-label="Общий размер новых файлов, байт"
                                        aria-valuemin={0}
                                        aria-valuemax={maxSizeBytes}
                                        aria-valuenow={Math.min(totalSize, maxSizeBytes)}
                                        aria-valuetext={`${formatBytes(totalSize)} из ${formatBytes(maxSizeBytes)}`}
                                    >
                                        <div
                                            className="attach-files-block__usage-fill"
                                            style={{ width: `${percent}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
            {removed && (
                <div className="attach-files-block__undo" role="status">
                    <span>Файл «{removed.row.AttachmentFileName ?? removed.row._filename}» убран</span>
                    <Button size="xs" variant="text" onClick={restoreRemoved}>
                        Отменить
                    </Button>
                </div>
            )}
            {props.note && (
                <div className="attach-files-block__note" role="note">
                    <MdOutlineInfo aria-hidden />
                    {props.note}
                </div>
            )}
        </div>
    );
};
